import { useMemo } from 'react'
import { AlertTriangle, Calculator } from 'lucide-react'
import { Label } from '@/components/ui/label'
import type { RefFormGroup } from './FormFieldRefSelect'
import { CrossFormHints, FormulaEditor, type FormulaOperand } from './FormulaEditor'
import type { FormField } from './types'
import type { CrossFormAuthoring } from '@/hooks/useCrossFormAuthoring'
import {
  OPERAND_TYPES,
  buildCellsByKey,
  buildCrossLabels,
  buildRefGroups,
  buildTableSources,
  buildTotalLabels,
  calculatedFieldCanRead,
  initialTablePick,
  notApplicableRefs,
  tableFieldRefLabels,
  tableRefKey,
} from './formulaRefPicker'
import { TableRefPicker } from './TableRefPicker'
import { NotApplicableWarning } from './CellFormulaBuilder'
import { calculatedCellNodes, formulaReachesItself } from '@/lib/tableForm'
import type { CalcField } from '@/lib/formula'
import type { TableCellType } from '@/types/cgt'

interface Props {
  field: FormField
  allFields: FormField[]
  onChange: (patch: Partial<FormField>) => void
  /** Shown in the variable picker's form select ("This form — <name>"). */
  currentFormName?: string
  /**
   * Cross-form reference authoring (templates containing this form + their
   * upstream forms, scoped by the toolbar template context). Absent or context
   * "None" → the picker offers only this form's fields.
   */
  crossForm?: CrossFormAuthoring
}

// Field types that feed arithmetic (vs. text-like fields, which only feed
// string concatenation via the `+` operator).
const NUMERIC_TYPES: FormField['type'][] = ['number', 'calculated']
// Table cells a calculated field may read: the ones that hold a number.
const NUMERIC_CELL_TYPES: TableCellType[] = ['number', 'calculated']

/**
 * Does calculated field `candidate` (transitively) depend on `targetId`?
 * Used to hide operands that would create a cycle (target → candidate → target).
 */
function dependsOn(
  candidate: FormField,
  targetId: string,
  byId: Map<string, FormField>,
  seen = new Set<string>(),
): boolean {
  if (candidate.type !== 'calculated' || !candidate.formula) return false
  for (const m of candidate.formula.matchAll(/\{([^}]+)\}/g)) {
    const refId = m[1]
    if (refId === targetId) return true
    if (seen.has(refId)) continue
    seen.add(refId)
    const ref = byId.get(refId)
    if (ref && dependsOn(ref, targetId, byId, seen)) return true
  }
  return false
}

/** Formula editor for an ordinary calculated field. */
export function FormulaBuilder({ field, allFields, onChange, currentFormName, crossForm }: Props) {
  const byId = useMemo(() => new Map(allFields.map(f => [f.id, f])), [allFields])
  const formula = field.formula ?? ''

  // Labels for cross-form refs already stored in the formula, "Label (Form)".
  const crossLabels = useMemo(() => buildCrossLabels(formula, crossForm), [formula, crossForm])

  // The form's table fields: their cells ("Vitals › Weight @ Day 1") and
  // column and row totals ("Sum of Weight (Vitals)").
  const tables = useMemo(() => buildTableSources(allFields), [allFields])
  const cellsByKey = useMemo(() => buildCellsByKey(tables), [tables])
  const totalLabels = useMemo(() => buildTotalLabels(tables), [tables])

  // A cell marked not applicable is not a valid ref, but its chip keeps its
  // name: the not applicable warning below says what is wrong with it.
  const labelById = useMemo(() => {
    const m: Record<string, string> = { ...crossLabels, ...tableFieldRefLabels(allFields) }
    for (const [key, cell] of cellsByKey) if (cell.disabled) m[key] = cell.label
    for (const f of allFields) m[f.id] = f.label || 'Untitled'
    return m
  }, [allFields, crossLabels, cellsByKey])

  // Calculated fields (this one with its live formula) and calculated cells
  // are evaluated in one dependency order at fill time, so a loop through
  // either leaves the whole chain blank.
  const calcNodes = useMemo<CalcField[]>(
    () => [
      ...allFields
        .filter(f => f.type === 'calculated')
        .map(f => ({
          id: f.id,
          label: f.label,
          fieldType: 'calculated',
          formula: f.id === field.id ? formula : f.formula,
        })),
      ...calculatedCellNodes(allFields.map(f => ({ fieldType: f.type, tableConfig: f.tableConfig }))),
    ],
    [allFields, field.id, formula],
  )
  const isCircular = useMemo(() => formulaReachesItself(calcNodes, field.id), [calcNodes, field.id])
  const naRefs = useMemo(() => notApplicableRefs(formula, cellsByKey), [formula, cellsByKey])

  const cellAllowed = (key: string) => calculatedFieldCanRead(cellsByKey, calcNodes, field.id, key)

  // Fields that can feed this formula (excluding self and cycle-creating calcs).
  const operandFields = useMemo(
    () =>
      allFields.filter(
        f =>
          f.id !== field.id &&
          OPERAND_TYPES.includes(f.type) &&
          !dependsOn(f, field.id, byId),
      ),
    [allFields, field.id, byId],
  )

  // Hierarchical variable picker groups: this form first, then the forms
  // earlier in the toolbar's template context (empty when context = None).
  // Tables are listed ahead of the fields; picking one reveals its cells and
  // totals (TableRefPicker).
  const refGroups = useMemo<RefFormGroup[]>(() => {
    const [self, ...upstream] = buildRefGroups({ operandFields, currentFormName, crossForm })
    const tableEntries = tables.map(t => ({ refKey: tableRefKey(t), label: t.label, type: 'table' }))
    return [{ ...self, fields: [...tableEntries, ...self.fields] }, ...upstream]
  }, [operandFields, currentFormName, crossForm, tables])

  const operands = useMemo<FormulaOperand[]>(
    () => [
      ...[...cellsByKey.keys()].filter(cellAllowed).map(key => ({ id: key, label: labelById[key] })),
      ...Object.entries(totalLabels).map(([id, label]) => ({ id, label })),
      ...operandFields.map(f => ({ id: f.id, label: f.label || 'Untitled' })),
    ],
    // cellAllowed reads cellsByKey, calcNodes and field.id.
    [cellsByKey, calcNodes, field.id, labelById, totalLabels, operandFields],
  )
  // Earlier forms' fields (labels carry the form name) — same template context
  // as the main picker.
  const conditionOperands = useMemo<FormulaOperand[]>(
    () => [
      ...operands,
      ...(crossForm?.available && crossForm.selectedProcedureTemplateId
        ? crossForm.allUpstreamFields.map(f => ({ id: f.refKey, label: f.label }))
        : []),
    ],
    [operands, crossForm],
  )

  return (
    <FormulaEditor
      header={
        <Label className='flex items-center gap-1.5'>
          <Calculator className='h-3.5 w-3.5' />
          Calculate from
        </Label>
      }
      formula={formula}
      onFormulaChange={f => onChange({ formula: f })}
      displayFormat={field.displayFormat}
      defaultDisplayFormat='decimal3'
      onDisplayFormatChange={displayFormat => onChange({ displayFormat })}
      labels={labelById}
      isCrossRef={id => crossLabels[id] !== undefined}
      isNumericRef={id => {
        const cell = cellsByKey.get(id)
        if (cell) return NUMERIC_CELL_TYPES.includes(cell.type)
        if (totalLabels[id] !== undefined) return true
        return NUMERIC_TYPES.includes(byId.get(id)?.type ?? 'text')
      }}
      operands={operands}
      conditionOperands={conditionOperands}
      emptyHint='Click a field, number, or text below to start…'
      brokenLabel='⚠ deleted field'
      refNoun={['field', 'fields']}
      hasExtraError={isCircular || naRefs.length > 0}
      extraWarnings={
        <>
          {isCircular && (
            <div className='flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs text-destructive'>
              <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
              <span>
                This formula refers back to this field through table cells or other calculated
                fields, so it can never be worked out. Remove one of the references in the loop.
              </span>
            </div>
          )}
          {naRefs.length > 0 && <NotApplicableWarning labels={naRefs} />}
        </>
      }
      renderRefEditor={(id, replace) => (
        <TableRefPicker
          groups={refGroups}
          tables={tables}
          initial={initialTablePick(id, tables, labelById[id] !== undefined ? id : '')}
          cellAllowed={cellAllowed}
          actionLabel='Apply'
          size='sm'
          onPick={replace}
          formula={formula}
          owner={field.id}
        />
      )}
      renderReferencePicker={insert => (
        // Searchable form select, then searchable field select. Picking a field
        // appends it to the formula; the form choice is sticky so several
        // fields can be added in a row.
        <div className='space-y-1.5'>
          <p className='text-xs text-muted-foreground'>Add a field to the formula:</p>
          {refGroups[0].fields.length === 0 && refGroups.length === 1 ? (
            <p className='text-xs text-muted-foreground italic'>
              Add another field to this form to reference it here.
            </p>
          ) : (
            <TableRefPicker
              groups={refGroups}
              tables={tables}
              initial={{ ref: '' }}
              cellAllowed={cellAllowed}
              actionLabel='Insert'
              onPick={insert}
              formula={formula}
              owner={field.id}
            />
          )}
          <CrossFormHints crossForm={crossForm} hasUpstream={refGroups.length > 1} />
        </div>
      )}
    />
  )
}
